import {
  CSSProperties,
  Fragment,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cc from 'classcat';
import 'swiper/css';
import 'swiper/css/grid';
import { Autoplay, Grid, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Swiper as ISwiper } from 'swiper/types';
import 'theme/swiper.css';
import { EIconSize } from 'utils/constants/app/ui';
import { timeoutDuration } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import RidGenerator from 'utils/swarm/rid-generator';
import { GlobalIcon } from 'components/GlobalIcon';
import { Portal } from 'components/Shared/Portal';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TSwiperSliderProps = {
  delay?: number;
  autoplay?: boolean;
  activeSlide: number;
  bodyArrows?: boolean;
  headerTitle?: string | ReactNode;
  headerArrows?: boolean;
  slideClassName?: string;
  showIndicators?: boolean;
  sliderClassName?: string;
  disableSwiping?: boolean;
  drawableTabs: ReactNode[];
  arrowsPortalId?: string | null;
  sliderWrapperClassName?: string;
  interactiveIndicators?: boolean;
  indicatorsCustomClassName?: string;
  slidesPerView?: number | 'auto' | undefined;
  loop?: boolean;
  style?: CSSProperties;
  spaceBetween?: number;
  rows?: number;
  fetchOnProgress?: {
    point: number;
    callback: () => void;
  };
  alwaysShowArrows?: boolean;
  slideOnHover?: boolean;
  notToWatchIsEnd?: boolean;
  arrowIconSize?: EIconSize;
  arrowColor?: string;
  animationSpeed?: number;
};

const SLIDER_DEFAULT_SPEED = 300;
const SWIPER_AUTOPLAY_DELAY = 1000;
const UNIQUE_CLASS_STRINGIFY_RADIX = 36;
const UNIQUE_CLASS_CUT_START_POINT = 7;
const getSliderSelectorClassName = (id: string | null) =>
  id ? `SLIDER_SELECTOR_${id}` : null;

const getSlideSelectorClassName = (id: string | null) =>
  id ? `SLIDE_SELECTOR_${id}` : '';

export const SwiperSlider = ({
  delay,
  autoplay,
  bodyArrows,
  headerTitle,
  activeSlide,
  headerArrows,
  drawableTabs,
  slidesPerView,
  showIndicators,
  slideClassName = '',
  sliderClassName = '',
  arrowsPortalId = null,
  disableSwiping = false,
  interactiveIndicators = false,
  indicatorsCustomClassName = '',
  loop = false,
  sliderWrapperClassName = '',
  style,
  spaceBetween,
  rows = 1,
  fetchOnProgress,
  alwaysShowArrows,
  slideOnHover = false,
  notToWatchIsEnd = false,
  arrowIconSize = EIconSize._20,
  arrowColor,
  animationSpeed = SLIDER_DEFAULT_SPEED
}: TSwiperSliderProps) => {
  const [isEnd, setIsEnd] = useState(false);
  const sliderRef = useRef<SwiperRef | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bodyArrowRef = useRef<HTMLDivElement | null>(null);
  const [isArrowsNeeded, setIsArrowsNeeded] = useState(true);
  const [isSlidesRendered, setIsSlidesRendered] = useState(false);

  const sliderId = useRef(RidGenerator.gCustom());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    const items =
      containerRef.current?.querySelector('.swiper-wrapper')?.childNodes;

    if (items) {
      const changeSize = () => {
        const containerWidth = containerRef.current?.clientWidth || 0;

        let visibleItemsCount = 0;
        let totalWidth = 0;

        for (let i = 0; i < items.length; i++) {
          const itemWidth = (items[i] as HTMLElement)?.offsetWidth;

          if (totalWidth + itemWidth > containerWidth) {
            break;
          }

          visibleItemsCount++;
          totalWidth += itemWidth;
        }

        setIsArrowsNeeded(drawableTabs.length > visibleItemsCount);
      };

      changeSize();
      window.addEventListener('resize', changeSize);

      return () => {
        window.removeEventListener('resize', changeSize);
      };
    }
  }, [drawableTabs.length]);

  useEffect(() => {
    if (sliderRef.current?.swiper && activeSlide) {
      sliderRef.current.swiper.slideTo(activeSlide, 0);
      setActiveIndex(activeSlide);
    }
  }, [activeSlide]);

  const paginationUniqueClass = useMemo(() => {
    return `swiper-pagination-${(Math.random() + 1)
      .toString(UNIQUE_CLASS_STRINGIFY_RADIX)
      .substring(UNIQUE_CLASS_CUT_START_POINT)}`;
  }, []);

  const handlePrevClick = (e: SyntheticEvent) => {
    e.stopPropagation();

    if (sliderRef.current?.swiper) {
      sliderRef.current.swiper.slidePrev();
    }
  };

  const handleNextClick = (e: SyntheticEvent) => {
    e.stopPropagation();

    if (sliderRef.current?.swiper) {
      sliderRef.current.swiper.slideNext();

      if (!notToWatchIsEnd) {
        setIsEnd(sliderRef.current.swiper.isEnd);
      }
    }
  };

  const handleSwiperInit = useCallback((swiper: ISwiper) => {
    if (!notToWatchIsEnd) {
      setIsEnd(swiper.isEnd);
    }
  }, []);

  useEffect(() => {
    setIsSlidesRendered(true);

    return () => {
      setIsSlidesRendered(false);

      sliderRef.current?.swiper?.updateSlides();
    };
  }, [drawableTabs]);

  const slides = useMemo(() => {
    if (!drawableTabs || !drawableTabs.length) {
      return [];
    }

    const shouldDuplicateSlidesCount =
      isSlidesRendered && slidesPerView === 'auto' && autoplay;

    if (!shouldDuplicateSlidesCount) {
      return drawableTabs;
    }

    const slide = document.querySelector(
      `.${getSlideSelectorClassName(sliderId.current)}`
    );

    const slider = document.querySelector(
      `.${getSliderSelectorClassName(sliderId.current)}`
    );

    if (slide && slider) {
      const slideWidth = slide.getClientRects()[0]?.width;
      const sliderWidth = slider.getClientRects()[0]?.width;

      const slidePeerView = Math.floor(sliderWidth / slideWidth);

      if (
        ((autoplay || loop) &&
          slidesPerView === 'auto' &&
          drawableTabs.length > slidePeerView) ||
        (drawableTabs.length > slidePeerView &&
          drawableTabs.length < slidePeerView * 2)
      ) {
        return [...drawableTabs, ...drawableTabs].map((node, index) => (
          <Fragment key={index}>{node}</Fragment>
        ));
      }
    }

    return drawableTabs;
  }, [
    autoplay,
    drawableTabs,
    slidesPerView,
    isSlidesRendered,
    sliderId.current
  ]);

  const handleSlideChange = useCallback((swiper: ISwiper) => {
    if (!notToWatchIsEnd) {
      setIsEnd(swiper.isEnd);
    }

    setActiveIndex(swiper.activeIndex);
  }, []);

  const ArrowsWrapper = useCallback(
    ({ children }: { children: ReactElement }) =>
      arrowsPortalId ? (
        <Portal rootId={arrowsPortalId}>{children}</Portal>
      ) : (
        children
      ),
    [arrowsPortalId]
  );

  const handleHoverPrev = (e: SyntheticEvent) => {
    handlePrevClick(e);
    intervalRef.current = setInterval(() => {
      handlePrevClick(e);
    }, timeoutDuration.DURATION_1000);
  };

  const handleHoverNext = (e: SyntheticEvent) => {
    handleNextClick(e);
    intervalRef.current = setInterval(() => {
      handleNextClick(e);
    }, timeoutDuration.DURATION_1000);
  };

  const handleMouseLeave = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const isLeftArrowDisabled = autoplay || loop ? false : activeIndex === 0;
  const isRightArrowDisabled =
    autoplay || loop ? false : sliderRef?.current?.swiper?.isEnd || isEnd;

  return (
    <div
      ref={containerRef}
      className={sliderWrapperClassName}
      style={style || {}}
      onMouseOver={() => {
        if (
          !SpringConfigs?.CAROUSEL_ARROWS &&
          bodyArrowRef.current &&
          !alwaysShowArrows
        ) {
          bodyArrowRef.current.style.display = 'block';
        }
      }}
      onMouseOut={() => {
        if (
          !SpringConfigs?.CAROUSEL_ARROWS &&
          bodyArrowRef.current &&
          !alwaysShowArrows
        ) {
          bodyArrowRef.current.style.display = 'none';
        }
      }}
    >
      {headerArrows ? (
        <div className="swiper__slider--header">
          <h3>{headerTitle}</h3>
          {(alwaysShowArrows || isArrowsNeeded) && (
            <div className="swiper__slider--header__arrowsRow">
              <button
                onClick={handlePrevClick}
                disabled={isLeftArrowDisabled}
                className={cc([
                  'swiperSliderArrowLeft',
                  { 'disabled-arrow': isLeftArrowDisabled }
                ])}
              >
                <GlobalIcon
                  lib="generic"
                  name="caretsmall"
                  theme="default"
                  size={arrowIconSize}
                  color={arrowColor}
                />
              </button>

              <button
                className={cc([
                  'swiperSliderArrowRight',
                  { 'disabled-arrow': isRightArrowDisabled }
                ])}
                onClick={handleNextClick}
                disabled={isRightArrowDisabled}
              >
                <GlobalIcon
                  lib="generic"
                  name="caretsmall"
                  theme="default"
                  size={arrowIconSize}
                  color={arrowColor}
                />
              </button>
            </div>
          )}
        </div>
      ) : null}

      <div className="swiperWrapper">
        {bodyArrows && (alwaysShowArrows || isArrowsNeeded) && (
          <ArrowsWrapper>
            <div
              style={{
                display:
                  SpringConfigs?.CAROUSEL_ARROWS || alwaysShowArrows
                    ? 'block'
                    : 'none'
              }}
              ref={bodyArrowRef}
            >
              <div
                className="swiperWrapper__arrows"
                style={{ display: isLeftArrowDisabled ? 'none' : 'block' }}
              >
                <button
                  {...(slideOnHover
                    ? {
                        onMouseEnter: handleHoverPrev,
                        onMouseLeave: handleMouseLeave
                      }
                    : { onClick: handlePrevClick })}
                  data-testid="carousel-arrow-left"
                  disabled={isLeftArrowDisabled}
                  className={cc([
                    'swiperSliderArrowLeft',
                    { 'disabled-arrow': isLeftArrowDisabled }
                  ])}
                >
                  <GlobalIcon
                    lib="generic"
                    name="caretsmall"
                    theme="default"
                    size={arrowIconSize}
                    color={arrowColor}
                  />
                </button>
              </div>
              <div
                className="swiperWrapper__arrows"
                style={{ display: isRightArrowDisabled ? 'none' : 'block' }}
              >
                <button
                  {...(slideOnHover
                    ? {
                        onMouseEnter: handleHoverNext,
                        onMouseLeave: handleMouseLeave
                      }
                    : { onClick: handleNextClick })}
                  data-testid="carousel-arrow-right"
                  className={cc([
                    'swiperSliderArrowRight',
                    { 'disabled-arrow': isRightArrowDisabled }
                  ])}
                  disabled={isRightArrowDisabled}
                >
                  <GlobalIcon
                    lib="generic"
                    name="caretsmall"
                    theme="default"
                    size={arrowIconSize}
                    color={arrowColor}
                  />
                </button>
              </div>
            </div>
          </ArrowsWrapper>
        )}

        <Swiper
          speed={animationSpeed}
          loop={autoplay || loop}
          className={`${getSliderSelectorClassName(
            sliderId.current
          )} ${sliderClassName}`}
          allowTouchMove={!disableSwiping}
          pagination={{
            type: 'bullets',
            el: `.${paginationUniqueClass}`,
            clickable: interactiveIndicators
          }}
          autoplay={
            autoplay && {
              delay: delay || SWIPER_AUTOPLAY_DELAY,
              disableOnInteraction: false
            }
          }
          slidesPerView={slidesPerView ? slidesPerView : 'auto'}
          onSlideChange={handleSlideChange}
          onSwiper={handleSwiperInit}
          ref={sliderRef}
          onSliderMove={sliderRef.current?.swiper?.update}
          spaceBetween={spaceBetween}
          grid={{
            rows: rows,
            fill: 'row'
          }}
          modules={[Grid, Navigation, Autoplay, Pagination]}
          // please use only with react query to avoid duplicate calls
          onProgress={val => {
            if (
              fetchOnProgress?.point &&
              +val.progress.toFixed(2) > fetchOnProgress.point
            ) {
              fetchOnProgress?.callback();
            }
          }}
        >
          {slides?.map((item, index) => {
            return (
              <SwiperSlide
                className={`${getSlideSelectorClassName(
                  sliderId.current
                )} ${slideClassName} `}
                key={index}
              >
                <div>{item}</div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
      {showIndicators && (
        <div
          className={`swiperWrapper__swiper-pagination swiper-pagination ${paginationUniqueClass} ${indicatorsCustomClassName}`}
        />
      )}
    </div>
  );
};
